/**
 * Pattern store — the active pattern is always a PatternConfig (wallpaper group
 * + lattice params); the tile is the group's fundamental domain. currentPattern
 * / currentVariant derive from it so TileCanvas, useDrawing and useWrapTransform
 * work unchanged.
 */
import type { TessellationPattern } from '@/patterns/types';
import {
    WALLPAPER_GROUPS,
    buildConfigVariant,
    defaultLatticeParams,
    getSystemPreset,
    getWallpaperGroup,
    isWpgFree,
    type LatticeParams,
    type PatternConfig,
    type WallpaperGroupId,
} from '@/patterns/wallpaper';
import { useEntitlementStore } from '@/stores/entitlement';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

const DEFAULT_CONFIG = getSystemPreset('sys:hex-p3')!;

export const usePatternStore = defineStore('pattern', () => {
  /** Active configuration. */
  const currentConfig = ref<PatternConfig>(DEFAULT_CONFIG);
  /** Legacy ids kept for sketch metadata (both hold the wallpaper-group id). */
  const currentPatternId = ref(DEFAULT_CONFIG.wpgId);
  const currentVariantId = ref(DEFAULT_CONFIG.wpgId);
  const tileSize = ref(120); // px, 60–320

  const built = computed(() => buildConfigVariant(currentConfig.value));

  const currentPattern = computed<TessellationPattern>(() => ({
    id: `cfg:${currentConfig.value.id}`,
    name: currentConfig.value.label,
    description: built.value.variant.description,
    icon: '',
    free: isWpgFree(currentConfig.value.wpgId),
    polygon: () => built.value.polygon,
    variants: [built.value.variant],
  }));

  const currentVariant = computed(() => built.value.variant);

  /** Apply a config without an entitlement check (live preview, favourites). */
  function applyConfig(config: PatternConfig): void {
    currentConfig.value = config;
    currentPatternId.value = config.wpgId;
    currentVariantId.value = config.wpgId;
  }

  /**
   * Apply a config, gated on the group's free tier (the belt behind the panel).
   */
  function setConfig(config: PatternConfig): boolean {
    const entitlement = useEntitlementStore();
    if (!isWpgFree(config.wpgId) && !entitlement.isPro) return false;
    applyConfig(config);
    return true;
  }

  /** Restore a pattern from a saved sketch (stored as a wallpaper-group id).
   *  `savedLatticeParams` restores the grid the sketch was drawn on; merged over
   *  the group defaults so a partial/legacy record still yields a complete set. */
  function applyWpg(wpgId: string, savedArtistTile?: string, savedLatticeParams?: LatticeParams): void {
    if (!(wpgId in WALLPAPER_GROUPS)) return; // tolerate ids from older sketches
    const id = wpgId as WallpaperGroupId;
    // Validate savedArtistTile is one of the allowed values
    const validTiles = ['standard', 'hexagon', 'rhombus', 'parallelogram'];
    const artistTile = savedArtistTile && validTiles.includes(savedArtistTile)
      ? (savedArtistTile as any)
      : 'standard';
    applyConfig({
      id: `open:${id}`,
      label: getWallpaperGroup(id).label,
      wpgId: id,
      latticeParams: { ...defaultLatticeParams(id), ...(savedLatticeParams ?? {}) },
      artistTile,
      isSystem: false,
    });
  }

  return {
    currentPatternId,
    currentVariantId,
    tileSize,
    currentConfig,
    currentPattern,
    currentVariant,
    applyConfig,
    setConfig,
    applyWpg,
  };
});
